import { ADMIN_QUERY_KEYS } from '@/api/queries/admin';
import * as inviteApi from '@/api/endpoints/admin-invites';
import type { ListInvitesParams, CreateInviteParams } from '@/api/endpoints/admin-invites';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/components/ui/toast';

export function useAdminListInvites(params: ListInvitesParams = {}) {
  return useQuery({
    queryKey: ADMIN_QUERY_KEYS.invitesList(params),
    queryFn: () => inviteApi.listInvites(params),
    staleTime: 1000 * 60 * 2,
    meta: { persist: false },
  });
}

export function useAdminInviteStats() {
  return useQuery({
    queryKey: ADMIN_QUERY_KEYS.inviteStats(),
    queryFn: () => inviteApi.getInviteStats(),
    staleTime: 1000 * 60 * 2,
    meta: { persist: false },
  });
}

export function useAdminCreateInvite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateInviteParams) => inviteApi.createInvite(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.invites() });
      queryClient.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.inviteStats() });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to create invite');
    },
  });
}

export function useAdminSendInviteEmail() {
  return useMutation({
    mutationFn: (code: string) => inviteApi.sendInviteEmail(code),
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to send invite email');
    },
  });
}

export function useAdminUpdateInviteEmail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ code, email }: { code: string; email: string | null }) =>
      inviteApi.updateInviteEmail(code, { email }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.invites() });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update invite email');
    },
  });
}

export function useAdminDeleteInvite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (code: string) => inviteApi.deleteInvite(code),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.invites() });
      queryClient.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.inviteStats() });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete invite');
    },
  });
}
